import { useMutation } from '@tanstack/react-query'
import { isProblemError, verifyPassword } from '@volee/api-client'
import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'

import type { PasswordFields } from './password-check-dialog.tsx'
import { errorMessage } from '../messages/from-error.ts'
import { api } from '../services/api.ts'
import { registerPasswordAsker } from '../services/password-check.ts'

// The question is its own chunk: a Radix dialog the anonymous funnel would download and never draw.
const PasswordCheckDialog = lazy(() =>
  import('./password-check-dialog.tsx').then((m) => ({ default: m.PasswordCheckDialog })),
)

function isWrongPassword(error: unknown): boolean {
  return isProblemError(error) && error.code === 'auth.password_wrong'
}

/** What `withRecentPassword` asks through: mounted once by the shell, it opens the question when an act is
 *  refused for want of the password, checks the answer with the API (never `signIn`, which would replace the
 *  session on the phone) and answers the act that asked. Two acts asking at once share the one question. */
export function PasswordCheckHost() {
  const [open, setOpen] = useState(false)
  const settle = useRef<((given: boolean) => void) | null>(null)
  const asking = useRef<Promise<boolean> | null>(null)
  const form = useForm<PasswordFields>({ defaultValues: { password: '' } })
  const check = useMutation({ mutationFn: (password: string) => verifyPassword(api, password) })

  useEffect(() => {
    registerPasswordAsker(() => {
      if (asking.current !== null) return asking.current
      form.reset({ password: '' })
      check.reset()
      asking.current = new Promise<boolean>((resolve) => {
        settle.current = (given) => {
          asking.current = null
          settle.current = null
          setOpen(false)
          resolve(given)
        }
      })
      setOpen(true)
      return asking.current
    })
    return () => {
      registerPasswordAsker(null)
    }
    // `form` and `check` are stable handles, and the asker must not be registered again on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function submit(fields: PasswordFields) {
    check.mutate(fields.password, {
      onSuccess: () => {
        settle.current?.(true)
      },
      onError: (error) => {
        if (isWrongPassword(error)) form.setError('password', { message: errorMessage(error) })
      },
    })
  }

  if (!open) return null
  const failure = check.error
  return (
    <Suspense fallback={null}>
      <PasswordCheckDialog
        form={form}
        checking={check.isPending}
        formError={failure === null || isWrongPassword(failure) ? undefined : errorMessage(failure)}
        onSubmit={submit}
        onCancel={() => {
          settle.current?.(false)
        }}
      />
    </Suspense>
  )
}
